import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';
import Input from './Input';
import { t } from '../../utils/translations';

const SearchCommandPalette = ({ isOpen = false, onClose }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentItems, setRecentItems] = useState([]);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  const searchCategories = [
    {
      id: 'pages',
      label: t('search.pages'),
      items: [
        { id: 'dashboard', label: t('nav.dashboard'), path: '/dashboard', icon: 'LayoutDashboard', description: t('search.mainOverview') },
        { id: 'articles', label: t('articles.management'), path: '/articles-management', icon: 'FileText', description: t('search.manageArticles') },
        { id: 'article-editor', label: t('nav.articleEditor'), path: '/article-editor', icon: 'Edit3', description: t('search.createAndEditArticles') },
        { id: 'projects', label: t('projects.management'), path: '/projects-management', icon: 'FolderOpen', description: t('search.manageUnionProjects') },
        { id: 'project-form', label: t('nav.projectForm'), path: '/project-form', icon: 'Plus', description: t('search.createNewProjects') },
        { id: 'sub-unions', label: t('subUnions.management'), path: '/sub-unions-management', icon: 'Building2', description: t('search.manageSubUnionBranches') },
        { id: 'sub-union-form', label: t('nav.subUnionForm'), path: '/sub-union-form', icon: 'Plus', description: t('search.createNewSubUnions') },
        { id: 'cities', label: t('cities.management'), path: '/cities-management', icon: 'MapPin', description: t('search.manageCityLocations') },
        { id: 'offices', label: t('offices.management'), path: '/offices-management', icon: 'Briefcase', description: t('search.manageOfficePositions') },
        { id: 'office-form', label: t('nav.officeForm'), path: '/office-form', icon: 'Plus', description: t('search.createNewOfficePositions') }
      ]
    },
    {
      id: 'actions',
      label: t('search.quickActions'),
      items: [
        { id: 'new-article', label: t('search.createNewArticle'), path: '/article-editor', icon: 'FileText', description: t('search.startWritingNewArticle') },
        { id: 'new-project', label: t('search.createNewProject'), path: '/project-form', icon: 'FolderPlus', description: t('search.initializeNewProject') },
        { id: 'new-sub-union', label: t('search.addSubUnion'), path: '/sub-union-form', icon: 'Building2', description: t('search.registerNewSubUnion') },
        { id: 'new-office', label: t('search.createOfficePosition'), path: '/office-form', icon: 'Briefcase', description: t('search.defineNewOfficeRole') },
        { id: 'settings', label: t('search.openSettings'), path: '/settings', icon: 'Settings', description: t('search.configureSystemSettings') },
        { id: 'help', label: t('search.getHelp'), path: '/help', icon: 'HelpCircle', description: t('search.accessHelpDocumentation') }
      ]
    }
  ];

  const filterResults = () => {
    if (!query?.trim()) {
      return recentItems?.length > 0 
        ? [{ id: 'recent', label: t('search.recent'), items: recentItems?.slice(0, 5) }]
        : searchCategories;
    }

    const filtered = searchCategories?.map(category => ({
      ...category,
      items: category?.items?.filter(item =>
        item?.label?.toLowerCase()?.includes(query?.toLowerCase()) ||
        item?.description?.toLowerCase()?.includes(query?.toLowerCase())
      )
    }))?.filter(category => category?.items?.length > 0);

    return filtered;
  };

  const getAllResults = () => {
    const categories = filterResults();
    const allItems = [];
    
    categories?.forEach(category => {
      category?.items?.forEach((item, index) => {
        allItems?.push({
          ...item,
          categoryId: category?.id,
          categoryLabel: category?.label,
          globalIndex: allItems?.length
        });
      });
    });
    
    return allItems;
  };

  const handleNavigation = (item) => {
    // Add to recent items
    const updatedRecent = [
      item,
      ...recentItems?.filter(recent => recent?.id !== item?.id)
    ]?.slice(0, 10);
    
    setRecentItems(updatedRecent);
    localStorage.setItem('searchRecentItems', JSON.stringify(updatedRecent));
    
    // Navigate
    window.location.href = item?.path;
    onClose();
  };

  const handleKeyDown = (e) => {
    const allResults = getAllResults();
    
    switch (e?.key) {
      case 'ArrowDown':
        e?.preventDefault();
        setSelectedIndex(prev => 
          prev < allResults?.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e?.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : allResults?.length - 1
        );
        break;
      case 'Enter':
        e?.preventDefault();
        if (allResults?.[selectedIndex]) {
          handleNavigation(allResults?.[selectedIndex]);
        }
        break;
      case 'Escape':
        onClose();
        break;
    }
  };

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      
      // Load recent items from localStorage
      const saved = localStorage.getItem('searchRecentItems');
      if (saved) {
        try {
          setRecentItems(JSON.parse(saved));
        } catch (e) {
          setRecentItems([]);
        }
      }
      
      // Focus input after animation
      setTimeout(() => {
        inputRef?.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  if (!isOpen) return null;

  const filteredCategories = filterResults();
  const allResults = getAllResults();

  return (
    <div className="fixed inset-0 z-300 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="flex items-start justify-center min-h-screen pt-20 px-4">
        <div className="w-full max-w-2xl bg-popover border border-border rounded-lg shadow-elevation-3 animate-slide-up">
          {/* Search Input */}
          <div className="flex items-center px-4 py-3 border-b border-border">
            <Icon name="Search" size={20} className="text-muted-foreground ml-3" />
            <input
              ref={inputRef}
              type="text"
              placeholder={t('search.placeholder')}
              value={query}
              onChange={(e) => setQuery(e?.target?.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 bg-transparent text-popover-foreground placeholder-muted-foreground outline-none text-sm"
            />
            <div className="flex items-center space-x-2 space-x-reverse text-xs text-muted-foreground">
              <kbd className="px-2 py-1 bg-muted rounded text-xs">↑↓</kbd>
              <span>{t('search.navigate')}</span>
              <kbd className="px-2 py-1 bg-muted rounded text-xs">↵</kbd>
              <span>{t('search.select')}</span>
              <kbd className="px-2 py-1 bg-muted rounded text-xs">esc</kbd>
              <span>{t('search.close')}</span>
            </div>
          </div>

          {/* Results */}
          <div 
            ref={resultsRef}
            className="max-h-96 overflow-y-auto py-2"
          >
            {filteredCategories?.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">{t('search.noResults')} "{query}"</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('search.trySearching')}
                </p>
              </div>
            ) : (
              filteredCategories?.map((category) => (
                <div key={category?.id} className="mb-4 last:mb-0">
                  <div className="px-4 py-2">
                    <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider font-caption">
                      {category?.label}
                    </h3>
                  </div>
                  
                  <div className="space-y-1">
                    {category?.items?.map((item) => {
                      const globalIndex = allResults?.findIndex(r => r?.id === item?.id);
                      const isSelected = globalIndex === selectedIndex;
                      
                      return (
                        <button
                          key={item?.id}
                          className={`w-full flex items-center space-x-3 space-x-reverse px-4 py-3 text-right transition-colors ${
                            isSelected
                              ? 'bg-accent text-accent-foreground'
                              : 'hover:bg-muted text-popover-foreground'
                          }`}
                          onClick={() => handleNavigation(item)}
                          onMouseEnter={() => setSelectedIndex(globalIndex)}
                        >
                          <Icon 
                            name={item?.icon} 
                            size={18} 
                            className={isSelected ? 'text-accent-foreground' : 'text-muted-foreground'}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {item?.label}
                            </p>
                            <p className={`text-xs truncate ${
                              isSelected ? 'text-accent-foreground/70' : 'text-muted-foreground'
                            }`}>
                              {item?.description}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2 space-x-reverse">
                            {category?.id === 'recent' && (
                              <Icon 
                                name="Clock" 
                                size={14} 
                                className={isSelected ? 'text-accent-foreground/50' : 'text-muted-foreground/50'}
                                title={t('search.recent')}
                              />
                            )}
                            <Icon 
                              name="ArrowLeft" 
                              size={14} 
                              className={isSelected ? 'text-accent-foreground/50' : 'text-muted-foreground/50'}
                            />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-border bg-muted/30">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center space-x-4 space-x-reverse">
                <span>{t('sidebar.syrianStudentUnion')} - {t('sidebar.adminPanel')}</span>
                <span>•</span>
                <span>{allResults?.length} {t('search.results')}</span>
              </div>
              
              <div className="flex items-center space-x-2 space-x-reverse">
                <kbd className="px-2 py-1 bg-background rounded text-xs">Ctrl</kbd>
                <span>+</span>
                <kbd className="px-2 py-1 bg-background rounded text-xs">K</kbd>
                <span>{t('search.toSearch')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Click outside to close */}
      <div 
        className="absolute inset-0 -z-10"
        onClick={onClose}
      />
    </div>
  );
};

export default SearchCommandPalette;